import { useEffect, lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { isFieldRole } from "@dker/core";
import { useAuthStore } from "@/stores/authStore";
import { ToastViewport } from "@/components/shared/ToastViewport";
import { MainLayout } from "@/components/layout/MainLayout";
import { LoadingScreen } from "@/components/shared/LoadingScreen";
import { useSystemThemeSync } from "@/hooks/useTheme";

const Login = lazy(() => import("@/pages/Login").then((m) => ({ default: m.Login })));
const DemoStart = lazy(() => import("@/pages/DemoStart").then((m) => ({ default: m.DemoStart })));
const Dashboard = lazy(() => import("@/pages/Dashboard").then((m) => ({ default: m.Dashboard })));
const Quotes = lazy(() => import("@/pages/Quotes").then((m) => ({ default: m.Quotes })));
const QuoteDetail = lazy(() => import("@/pages/QuoteDetail").then((m) => ({ default: m.QuoteDetail })));
const QuoteEditor = lazy(() => import("@/pages/QuoteEditor").then((m) => ({ default: m.QuoteEditor })));
const Invoices = lazy(() => import("@/pages/Invoices").then((m) => ({ default: m.Invoices })));
const InvoiceDetail = lazy(() => import("@/pages/InvoiceDetail").then((m) => ({ default: m.InvoiceDetail })));
const Clients = lazy(() => import("@/pages/Clients").then((m) => ({ default: m.Clients })));
const ClientNew = lazy(() => import("@/pages/ClientForm").then((m) => ({ default: m.ClientNew })));
const ClientEdit = lazy(() => import("@/pages/ClientForm").then((m) => ({ default: m.ClientEdit })));
const ClientDetail = lazy(() => import("@/pages/ClientDetail").then((m) => ({ default: m.ClientDetail })));
const Projects = lazy(() => import("@/pages/Projects").then((m) => ({ default: m.Projects })));
const ProjectNew = lazy(() => import("@/pages/ProjectNew").then((m) => ({ default: m.ProjectNew })));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail").then((m) => ({ default: m.ProjectDetail })));
const Expenses = lazy(() => import("@/pages/Expenses").then((m) => ({ default: m.Expenses })));
const PurchaseInvoices = lazy(() =>
  import("@/pages/PurchaseInvoices").then((m) => ({ default: m.PurchaseInvoices })),
);
const Catalog = lazy(() => import("@/pages/Catalog").then((m) => ({ default: m.Catalog })));
const Stock = lazy(() => import("@/pages/Stock").then((m) => ({ default: m.Stock })));
const Equipment = lazy(() => import("@/pages/Equipment").then((m) => ({ default: m.Equipment })));
const EquipmentDetail = lazy(() => import("@/pages/EquipmentDetail").then((m) => ({ default: m.EquipmentDetail })));
const EquipmentLabels = lazy(() => import("@/pages/EquipmentLabels").then((m) => ({ default: m.EquipmentLabels })));
const ClientPortal = lazy(() =>
  import("@/pages/ClientPortal").then((m) => ({ default: m.ClientPortal })),
);
const SubcontractorPlanning = lazy(() =>
  import("@/pages/SubcontractorPlanning").then((m) => ({ default: m.SubcontractorPlanning })),
);
const Suppliers = lazy(() => import("@/pages/Suppliers").then((m) => ({ default: m.Suppliers })));
const Subcontractors = lazy(() =>
  import("@/pages/Subcontractors").then((m) => ({ default: m.Subcontractors })),
);
const PurchaseOrders = lazy(() =>
  import("@/pages/PurchaseOrders").then((m) => ({ default: m.PurchaseOrders })),
);
const Alerts = lazy(() => import("@/pages/Alerts").then((m) => ({ default: m.Alerts })));
const EInvoices = lazy(() => import("@/pages/EInvoices").then((m) => ({ default: m.EInvoices })));
const Team = lazy(() => import("@/pages/Team").then((m) => ({ default: m.Team })));
const Timesheet = lazy(() => import("@/pages/Timesheet").then((m) => ({ default: m.Timesheet })));
const Planning = lazy(() => import("@/pages/Planning").then((m) => ({ default: m.Planning })));
const MyDay = lazy(() => import("@/pages/MyDay").then((m) => ({ default: m.MyDay })));
const Settings = lazy(() => import("@/pages/Settings").then((m) => ({ default: m.Settings })));
const NotFound = lazy(() => import("@/pages/NotFound").then((m) => ({ default: m.NotFound })));

/**
 * Une étiquette de machine scannée hors session : la fiche s'ouvre une fois
 * connecté, au lieu du tableau de bord. Seule la fiche d'une machine est
 * retenue — ce n'est pas une redirection générale.
 */
const APRES_CONNEXION = "dker.apres-connexion";
const FICHE_MACHINE = /^\/materiel\/[0-9a-f-]{36}$/;

/** Garde ?code= (PKCE) et #… du magic link en redirigeant vers /login */
function NavigateToLogin() {
  const { pathname, search, hash } = useLocation();
  if (FICHE_MACHINE.test(pathname)) {
    try {
      sessionStorage.setItem(APRES_CONNEXION, pathname);
    } catch {
      // Navigation privée : on atterrira sur l'accueil, rien de grave.
    }
  }
  return <Navigate to={{ pathname: "/login", search, hash }} replace />;
}

function NavigateAfterLogin() {
  let to = "/";
  try {
    const saved = sessionStorage.getItem(APRES_CONNEXION);
    if (saved && FICHE_MACHINE.test(saved)) to = saved;
  } catch {
    // Stockage indisponible : l'accueil.
  }
  // Effacé après le rendu, pas pendant : un rendu rejoué ne doit pas perdre
  // la fiche en route.
  useEffect(() => {
    try {
      sessionStorage.removeItem(APRES_CONNEXION);
    } catch {
      // Rien à effacer.
    }
  }, []);
  return <Navigate to={to} replace />;
}

export function App() {
  const status = useAuthStore((s) => s.status);
  const passwordRecovery = useAuthStore((s) => s.passwordRecovery);
  const init = useAuthStore((s) => s.init);
  // Avec les autres, et non plus bas : il y a un retour anticipé entre-temps,
  // et un hook conditionnel décale l'ordre des hooks d'un rendu à l'autre.
  const estTerrain = isFieldRole(useAuthStore((s) => s.user?.role));
  // Une session anonyme sans profil est une démonstration dont le bac à sable
  // a été purgé, ou pas encore préparé : elle n'a rien à faire sur l'écran de
  // création d'entreprise, que la base lui refuserait de toute façon.
  const demoSansBac = useAuthStore((s) => s.status === "unlinked" && s.authUser?.is_anonymous === true);

  // Monté ici et non dans MainLayout : l'écran de connexion est hors du
  // gabarit authentifié, et il doit suivre le thème comme le reste.
  useSystemThemeSync();

  useEffect(() => {
    void init();
  }, [init]);

  if (status === "loading") return <LoadingScreen />;

  const showAuthScreens = status === "anonymous" || status === "unlinked" || passwordRecovery;

  return (
    <>
      <Suspense fallback={<LoadingScreen />}>
        {showAuthScreens ? (
          <Routes>
            {/* Le suivi client vit dans les deux branches, et c'est voulu : il
                n'exige aucune session — c'est même son objet — et un patron
                connecté qui ouvre le lien qu'il vient d'envoyer doit voir ce
                que son client voit. */}
            <Route path="/suivi/:token" element={<ClientPortal />} />
            <Route path="/planning-sous-traitant/:token" element={<SubcontractorPlanning />} />
            <Route path="/demo" element={<DemoStart />} />
            <Route path="/login" element={demoSansBac ? <Navigate to="/demo" replace /> : <Login />} />
            <Route path="*" element={demoSansBac ? <Navigate to="/demo" replace /> : <NavigateToLogin />} />
          </Routes>
        ) : (
          <Routes>
            {/* Hors du `MainLayout` : le menu de l'entreprise n'a rien à faire
                autour de l'écran d'un client. */}
            <Route path="/suivi/:token" element={<ClientPortal />} />
            <Route path="/planning-sous-traitant/:token" element={<SubcontractorPlanning />} />
            <Route path="/demo" element={<DemoStart />} />
            <Route element={<MainLayout />}>
              {/* Le terrain a sa propre application, dans la même. Un compte
                  ouvrier n'a accès à rien de ce qui suit — la base le refuse
                  depuis 20260301001100 — donc l'envoyer sur le tableau de bord
                  lui montrerait des zéros partout. Il atterrit sur sa journée,
                  et les écrans de gestion l'y ramènent. */}
              <Route path="/ma-journee" element={<MyDay />} />
              <Route path="/" element={estTerrain ? <Navigate to="/ma-journee" replace /> : <Dashboard />} />
              <Route path="/devis" element={<Quotes />} />
              <Route path="/devis/new" element={<QuoteEditor />} />
              <Route path="/devis/:id/edit" element={<QuoteEditor />} />
              <Route path="/devis/:id" element={<QuoteDetail />} />
              <Route path="/factures" element={<Invoices />} />
              <Route path="/factures/:id" element={<InvoiceDetail />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/clients/new" element={<ClientNew />} />
              <Route path="/clients/:id" element={<ClientDetail />} />
              <Route path="/clients/:id/modifier" element={<ClientEdit />} />
              <Route path="/chantiers" element={<Projects />} />
              <Route path="/chantiers/new" element={<ProjectNew />} />
              <Route path="/chantiers/:id" element={<ProjectDetail />} />
              <Route path="/equipe" element={<Team />} />
              <Route path="/planning" element={<Planning />} />
              <Route path="/pointage" element={<Timesheet />} />
              <Route path="/depenses" element={<Expenses />} />
              <Route path="/achats" element={<PurchaseInvoices />} />
              <Route path="/catalogue" element={<Catalog />} />
              <Route path="/stock" element={<Stock />} />
              <Route path="/materiel" element={<Equipment />} />
              <Route path="/materiel/etiquettes" element={<EquipmentLabels />} />
              <Route path="/materiel/:id" element={<EquipmentDetail />} />
              <Route path="/fournisseurs" element={<Suppliers />} />
              <Route path="/sous-traitants" element={<Subcontractors />} />
              <Route path="/commandes" element={<PurchaseOrders />} />
              <Route path="/alertes" element={<Alerts />} />
              <Route path="/e-factures" element={<EInvoices />} />
              <Route path="/parametres" element={<Settings />} />
              {/* Toute route de gestion ramène le terrain chez lui plutôt que
                  sur une page vide qui ressemblerait à une panne. */}
              <Route
                path="*"
                element={estTerrain ? <Navigate to="/ma-journee" replace /> : <NotFound />}
              />
            </Route>
            <Route path="/login" element={<NavigateAfterLogin />} />
          </Routes>
        )}
      </Suspense>
      <ToastViewport />
    </>
  );
}
